import { reactive } from "vue";

/**
 * Client-side VPN Detection Utility for Vue Frontend
 *
 * Strategy:
 *  - Rely ONLY on public IP check (catches ALL VPN types: apps, extensions, corporate)
 *  - 2-minute cache prevents rate-limit errors (no more red "cancelled" in DevTools)
 *  - Network-change event fires instantly when user plugs in / removes VPN
 */

export const vpnState = reactive({
  isLocked: false,
  message: "",
  remainingSeconds: 15
});

// 4-second client-side cache so ip-api.com isn't hammered instantly
let _vpnCacheResult = null;
let _vpnCacheTime = 0;
const VPN_CACHE_TTL_MS = 4 * 1000; // 4 seconds

/**
 * Clear the VPN cache — must be called when the network changes
 * so the next check fetches a fresh result for the new IP.
 */
export function clearVpnCache() {
  _vpnCacheResult = null;
  _vpnCacheTime = 0;
}

/**
 * Detect VPN / Proxy via public IP check against ip-api.com.
 * Catches ALL VPN types: desktop apps, browser extensions, corporate tunnels.
 * Uses a 2-minute in-memory cache to avoid rate limits.
 */
export async function detectClientVpn(force = false) {
  const now = Date.now();

  // Return cached result if still fresh (and we aren't forcing a live check)
  if (!force && _vpnCacheResult !== null && now - _vpnCacheTime < VPN_CACHE_TTL_MS) {
    return _vpnCacheResult;
  }

  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 3000);

    const response = await fetch(
      "http://ip-api.com/json/?fields=status,proxy,org,as",
      { signal: controller.signal }
    );
    clearTimeout(timeoutId);

    if (response.ok) {
      const data = await response.json();
      if (data && data.status === "success") {
        const orgAsn = ((data.org || "") + " " + (data.as || "")).toLowerCase();

        // Strict VPN-specific keywords only — no "hosting", "aws", "amazon"
        // which cause false positives on corporate and mobile networks
        const strictVpnKeywords = [
          "vpn", "proxy", "nordvpn", "expressvpn", "surfshark",
          "mullvad", "protonvpn", "ipvanish", "m247", "hide.me",
          "windscribe", "cyberghost", "privateinternetaccess",
        ];
        const isVpnOrg = strictVpnKeywords.some((kw) => orgAsn.includes(kw));

        // Only block strict proxy=true flag — NOT hosting=true
        // (hosting=true causes false positives for iCloud Relay and mobile ISPs)
        if (data.proxy === true || isVpnOrg) {
          const result = { isVpn: true, message: "VPN or Proxy connection detected." };
          _vpnCacheResult = result;
          _vpnCacheTime = now;
          return result;
        }
      }
    }
  } catch (e) {
    // Network error, timeout, or rate limit cancelled:
    // We fail open for the background check, but we flag the error
    // so the popup countdown doesn't mistakenly think the VPN was turned off.
    return { isVpn: false, error: true };
  }

  const cleanResult = { isVpn: false, error: false };
  _vpnCacheResult = cleanResult;
  _vpnCacheTime = now;
  return cleanResult;
}

/**
 * Validate Geolocation position for mock/spoofing indicators
 */
export function validateClientLocation(position) {
  if (!position || !position.coords) {
    return { isFake: true, message: "Invalid or missing location coordinates." };
  }

  const { latitude, longitude, accuracy } = position.coords;

  // Null Island or out-of-bounds check
  if (
    Math.abs(latitude) > 90 ||
    Math.abs(longitude) > 180 ||
    (latitude === 0 && longitude === 0)
  ) {
    return {
      isFake: true,
      message: "Fake lat and long detected: Invalid or zero GPS coordinates. Attendance blocked.",
    };
  }

  // Exact 0 accuracy or explicit mock flag
  if (accuracy === 0 || position.coords.isMock === true) {
    return {
      isFake: true,
      message: "Fake lat and long detected: Mock/Spoofed location app active. Attendance blocked.",
    };
  }

  return { isFake: false };
}

/**
 * Un-bypassable background timer for VPN lockdown.
 * Runs in memory so it cannot be bypassed by deleting the UI overlay.
 */
let lockdownTimer = null;
let lockdownRecheck = null;

const handleNetworkChange = () => {
  clearVpnCache();
};

export function resetVpnLogoutState() {
  vpnState.isLocked = false;
  
  if (lockdownTimer) clearInterval(lockdownTimer);
  if (lockdownRecheck) clearInterval(lockdownRecheck);
  
  if (navigator.connection) {
    navigator.connection.removeEventListener("change", handleNetworkChange);
  }
  window.removeEventListener("online", handleNetworkChange);
  
  localStorage.removeItem("vpn_lock_start");
}

export function triggerVpnLogout(reason = "Security Policy Violation") {
  if (vpnState.isLocked) return;
  vpnState.message = reason;
  vpnState.isLocked = true;

  // F5 Refresh prevention
  const savedTime = localStorage.getItem("vpn_lock_start");
  const now = Date.now();
  if (savedTime) {
    const elapsed = Math.floor((now - parseInt(savedTime)) / 1000);
    vpnState.remainingSeconds = Math.max(0, 15 - elapsed);
  } else {
    localStorage.setItem("vpn_lock_start", now.toString());
    vpnState.remainingSeconds = 15;
  }

  // Network event listeners for instant disconnect detection
  if (navigator.connection) {
    navigator.connection.addEventListener("change", handleNetworkChange);
  }
  window.addEventListener("online", handleNetworkChange);

  // Background Un-bypassable Countdown (1 second)
  lockdownTimer = setInterval(() => {
    vpnState.remainingSeconds--;
    
    if (vpnState.remainingSeconds <= 0) {
      resetVpnLogoutState();
      localStorage.clear();
      sessionStorage.clear();
      window.location.href = "/LoginView";
    }
  }, 1000);

  // Fast Recheck (2 seconds)
  lockdownRecheck = setInterval(async () => {
    const recheck = await detectClientVpn(true); // force live check
    
    // If VPN is confirmed disconnected, cancel the lockdown instantly
    if (recheck && recheck.isVpn === false && recheck.error !== true) {
      resetVpnLogoutState();
    }
  }, 2000);
}
